/**
 * The character panel — **a status read, not a stat dump.**
 *
 * The distinction is the reading order. The old panel opened with eleven numbers and let the player rank
 * them; this opens with one interpreted sentence (*"Dehydrated. Badly wounded. Infection."*),
 * then the four bars that sentence was derived from, then everything else behind a disclosure.
 * A player who wants the numbers is two clicks from all of them; a player who wants to know
 * whether they can risk the culvert has already been told.
 *
 * **It is a drawer, and that is a deliberate non-decision about layout.** The mobile pass owns
 * the layout and says the panel becomes a bottom sheet, so committing to a docked sidebar now
 * would be building a two-column desktop grid for the mobile pass to delete. A drawer is
 * the same shape as `Journal.tsx`, works at every width, and costs nothing to convert. What it
 * does cost is that a closed panel cannot show the bars moving during a turn; that is the
 * player's choice while it is closed, and the bottom sheet with its peek row is where it stops being
 * one.
 *
 * The panel being *usable during a turn* is not incidental. The live turn's rule is that a running turn
 * disables exactly one thing — submitting a second action — and this is where that stops being
 * a claim about a boolean and starts being something a player can feel: they open the panel
 * thirty seconds into a fifty-second turn, while the DM is still composing, and watch thirst
 * climb.
 */
import { useState } from 'react';
import type { Change, CharacterSnapshot, GameTime } from '@/api/generated';
import { conditionClauses } from '@/features/character/condition';
import { Injuries } from '@/features/character/Injuries';
import { Inventory } from '@/features/character/Inventory';
import { markedSections } from '@/features/character/sections';
import { Skills } from '@/features/character/Skills';
import { StatusEffects } from '@/features/character/StatusEffects';
import { VitalBar } from '@/features/character/VitalBar';
import {
  polarityOf,
  readVitals,
  vitalLabelKey,
  type VitalReading,
} from '@/features/character/vitals';
import { gameTimeLabel } from '@/features/story/dateline';
import { suggestAction } from '@/features/turn/draft';
import { useT } from '@/i18n/context';
import { Dialog } from '@/ui/Dialog';
import { Nothing, Section } from '@/ui/Section';

interface CharacterPanelProps {
  /** Null while `GET /stories/{id}/state` is in flight. The toggle exists regardless. */
  character: CharacterSnapshot | null;
  time: GameTime | null;
  /**
   * The most recent turn's changes — live from `turn/store.ts` while one streams, and the
   * newest committed chapter's afterwards. Both drive the changed-this-chapter dots.
   */
  changes: readonly Change[];
  /**
   * Why there is no character, when there is no character (the loading-and-empty-states
   * checkbox). `GET /stories/{id}/state` can fail, and *"Reading the state…"* left on screen
   * for a request that already failed is a spinner that never resolves — the exact shape of
   * empty state that reads as a broken page. Null means it is genuinely still in flight.
   */
  note?: string | null;
  /**
   * Those changes are **not yet in `character`**: a turn is streaming them and the snapshot is
   * still the one from before it started. The invariant this maintains is worth stating,
   * because it is the reason this is a prop rather than something read from the store here —
   * *whatever the ticker in the feed is showing, the bars have applied.* The caller decides
   * when the live scene is on screen, so the caller decides this too.
   */
  pending: boolean;
}

export function CharacterPanel({
  character,
  time,
  changes,
  pending,
  note = null,
}: CharacterPanelProps) {
  const t = useT();
  const [open, setOpen] = useState(false);

  const close = (): void => {
    setOpen(false);
  };

  /**
   * items as affordances, and the two halves of it are equally load-bearing: write the opening, then get out
   * of the way. A suggestion that left the drawer up would have written into a box the player
   * cannot see, behind a backdrop they would have to dismiss before they could finish the
   * sentence — which is the affordance failing at the last step.
   *
   * The order matters and it is not arbitrary. `Dialog` restores focus to whatever opened it
   * when it unmounts, and the action bar focuses the box on the write; React runs an
   * unmounting effect's cleanup before the surviving effects of the same commit, so the box
   * wins and the caret lands where the player is about to type. `Inventory.test.tsx` asserts
   * exactly that, because it is the sort of ordering a later refactor breaks silently.
   */
  const suggest = (action: string): void => {
    suggestAction(action);
    close();
  };

  // A dot on the toggle itself, or a closed panel is a panel that never tells you anything
  // happened. Same rule as the sections: only news, never the clock.
  const marks = markedSections(changes);

  const toggle = (): void => {
    setOpen(!open);
  };

  const dot =
    marks.size > 0 && !open ? (
      <span
        className="bg-accent size-1.5 rounded-full"
        role="img"
        aria-label={t('ui.panel.changed')}
      />
    ) : null;

  return (
    <>
      {/*
       * **One toggle, two shapes.** Above `sm` it is the chip in the top-left corner it
       * has always been. Below it there is no corner to spend, and the panel is a sheet the
       * player has to pull up — so the toggle becomes the **peek row**: a full-width strip in
       * the sticky foot of the story screen with the four vitals drawn in it.
       *
       * That is a debt from the panel slice, which built the panel as a drawer and named exactly what it
       * cost: *"a closed panel cannot show the bars moving"* during a turn. On a phone the
       * panel is closed almost always, so on a phone the bars live out here.
       *
       * It is deliberately **one element** rather than a chip and a row with a breakpoint
       * hiding each. Two buttons for one action is two nodes in the accessibility tree, two
       * things for a test to disambiguate, and two places to keep the changed-this-chapter dot
       * in step; the two geometries differ by class, and the vitals themselves are the only
       * content that is mobile-only.
       */}
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className="border-rule bg-bg/95 hover:border-fg-faint sm:border-border sm:bg-bg/90 sm:text-fg-muted sm:hover:border-accent sm:hover:text-accent flex w-full items-center gap-3 border-t px-5 py-2 text-left font-mono backdrop-blur transition-colors sm:fixed sm:top-4 sm:left-4 sm:z-20 sm:w-auto sm:gap-2 sm:border sm:px-3 sm:py-1.5"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        <span className="text-fg-faint flex shrink-0 items-center gap-1.5 sm:text-inherit">
          {t('ui.nav.character')}
          {dot}
        </span>
        {character === null ? null : (
          /*
           * **The strip is hidden from the accessibility tree, on purpose.** It is a *glance*
           * — the affordance is "see the bars without opening the sheet", and there is no
           * glancing with a screen reader. Exposed, it would make the accessible name of this
           * button *"character, Health 58, Stamina 40, Hunger 58, Thirst 74"*, read out in
           * full every time focus lands on it, to say what the panel behind the same button
           * says properly, in bands and consequences rather than in bare integers.
           */
          <span aria-hidden="true" className="ml-auto flex min-w-0 items-center gap-3 sm:hidden">
            <VitalsPeek character={character} changes={changes} pending={pending} />
          </span>
        )}
      </button>

      <Dialog
        open={open}
        onClose={close}
        title={t('ui.nav.character')}
        variant="drawer"
        side="left"
      >
        {character === null ? (
          <Nothing>{note ?? t('ui.panel.reading_state')}</Nothing>
        ) : (
          <PanelBody
            character={character}
            time={time}
            changes={changes}
            pending={pending}
            marks={marks}
            onSuggest={suggest}
          />
        )}
      </Dialog>
    </>
  );
}

/**
 * Four bars, one row, no words — the vitals at a glance, for the layout where the panel they
 * live in is a sheet the player has to pull up.
 *
 * It reads `readVitals` with the same live changes the open panel does, so a turn moves this
 * row at the same moment it would move the full bars: the point of the peek row is that a
 * phone player watching a turn resolve sees thirst climb without opening anything.
 *
 * Its caller hides the whole strip from assistive technology, and the reason is written there.
 */
function VitalsPeek({
  character,
  changes,
  pending,
}: {
  character: CharacterSnapshot;
  changes: readonly Change[];
  pending: boolean;
}) {
  const t = useT();
  const readings = readVitals(character.vitals, pending ? changes : []);

  return (
    <>
      {readings.map((reading) => {
        const down = polarityOf(reading.name) === 'down';
        return (
          <span key={reading.name} className="flex w-12 flex-col gap-1">
            <span
              className="text-fg-muted flex justify-between font-mono tabular-nums"
              style={{ fontSize: '0.6875rem' }}
            >
              {/* Three letters, because four strips and a control have to fit across a
                  390-pixel screen and the bar under it carries the rest of the meaning. */}
              <span>{t(vitalLabelKey(reading.name)).slice(0, 3)}</span>
              <span className="text-fg">{reading.value}</span>
            </span>
            <span className="bg-rule relative block h-1 w-full overflow-hidden">
              <span
                className={`absolute inset-y-0 transition-[width] duration-500 ease-out ${down ? 'right-0' : 'left-0'}`}
                style={{
                  width: `${String(Math.max(0, Math.min(100, reading.value)))}%`,
                  backgroundColor: peekColor(reading),
                }}
              />
            </span>
          </span>
        );
      })}
    </>
  );
}

/**
 * The same two colour systems the full bar uses, reduced to what a 4-pixel strip can
 * carry: a quantity keeps its own hue, a warning takes the ramp — and the ramp is chosen by
 * the server's `band`, never by the number. The full bar's `BAND_RAMP` is not imported because
 * this needs three outcomes and that map exists to be exhaustive over sixteen bands; what is
 * shared is the rule, and the rule is written down in both places.
 */
function peekColor(reading: VitalReading): string {
  if (polarityOf(reading.name) === 'up') return `var(--color-vital-${reading.name})`;
  const band = reading.vital.band;
  if (band === 'starving' || band === 'dehydrated') return 'var(--color-ramp-danger)';
  if (band === 'very_hungry' || band === 'thirsty') return 'var(--color-ramp-warn)';
  return 'var(--color-ramp-ok)';
}

function PanelBody({
  character,
  time,
  changes,
  pending,
  marks,
  onSuggest,
}: {
  character: CharacterSnapshot;
  time: GameTime | null;
  changes: readonly Change[];
  pending: boolean;
  marks: ReadonlySet<string>;
  onSuggest: (action: string) => void;
}) {
  const t = useT();
  // The live overlay is applied only while the changes are genuinely uncommitted. After the
  // commit the snapshot already contains them, and `readVitals` reads absolute values rather
  // than deltas so a stray double-apply would be a no-op anyway — but not asking is clearer.
  const readings = readVitals(character.vitals, pending ? changes : []);
  const clauses = conditionClauses(t, character);

  return (
    <>
      <header className="mb-4">
        <p className="text-fg font-mono" style={{ fontSize: 'var(--text-action)' }}>
          {character.name}
        </p>
        <p className="text-fg-faint mt-0.5 font-mono" style={{ fontSize: 'var(--text-meta)' }}>
          {character.age} · {character.sex}
        </p>
        <p className="text-fg-muted mt-2 font-mono" style={{ fontSize: 'var(--text-meta)' }}>
          {character.location.display_name}
          {time !== null ? ` · ${gameTimeLabel(t, time)}` : ''}
        </p>
      </header>

      {/*
       * The condition line. Set in the interface face rather than the narration
       * serif on purpose: it is the game telling the player what its own numbers mean, which
       * is chrome speaking, not the world.
       */}
      <p
        className="border-rule text-fg mb-5 border-y py-3"
        style={{ fontSize: 'var(--text-action)', lineHeight: 1.5 }}
      >
        {clauses.length === 0
          ? t('ui.panel.condition_steady')
          : clauses.map((clause) => `${clause.text}.`).join(' ')}
      </p>

      <div className="mb-5">
        {readings.map((reading) => (
          <VitalBar key={reading.name} reading={reading} />
        ))}
      </div>

      <Section
        title={t('ui.panel.inventory')}
        count={character.inventory.length}
        marked={marks.has('inventory')}
      >
        <Inventory items={character.inventory} onSuggest={onSuggest} />
      </Section>

      <Section
        title={t('ui.panel.injuries')}
        count={character.injuries.length}
        marked={marks.has('injuries')}
      >
        <Injuries injuries={character.injuries} onSuggest={onSuggest} />
      </Section>

      <Section
        title={t('ui.panel.status_effects')}
        count={character.status_effects.length}
        marked={marks.has('effects')}
      >
        <StatusEffects effects={character.status_effects} />
      </Section>

      <Section
        title={t('ui.panel.skills')}
        count={character.skills.length}
        marked={marks.has('skills')}
      >
        <Skills skills={character.skills} />
      </Section>

      <Section
        title={t('ui.panel.places')}
        count={character.known_locations.length}
        marked={marks.has('places')}
      >
        <ul className="flex flex-col gap-1">
          {character.known_locations.map((place) => (
            <li
              key={place.name}
              className={place.name === character.location.name ? 'text-fg' : 'text-fg-muted'}
              style={{ fontSize: 'var(--text-meta)' }}
            >
              {place.display_name}
              {place.name === character.location.name ? (
                <span className="text-fg-faint font-mono"> · {t('ui.panel.here')}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}
